import React from 'react';
import { FieldInput } from '../../EmployeeData';
import { getExtraFieldInputName, recordTypeFormKey } from '../logic';
import {
  AvailableRecordTypeFragment,
  ExtraFieldsConfigReferenceInput,
  FieldType,
} from '../../../__generated__/graphql';
import Skeleton from 'react-loading-skeleton';
// eslint-disable-next-line no-restricted-imports -- v5 form stack, migrate to v7 (DX-194)
import { useFormContext } from 'react-hook-form-legacy';
import { ExtraFieldsVisibility } from '../extraFieldsVisibility';
import i18next from 'i18next';
import { ExtraFieldLookup } from '../ExtraFieldLookup/ExtraFieldLookup';
import { ExtraFieldContextParam, ExtraFieldFormInfoType } from '../types';
import { toHtmlInputStepAttribute } from './helpers';
import { RichtextInput } from '../../../../../components/form/RichtextInput';
import { ControlledInput } from '../../../../../components/form/Input';

export type Props = {
  availableRecordTypes: readonly AvailableRecordTypeFragment[];
  formFields: readonly ExtraFieldFormInfoType[];
  contextParams?: ExtraFieldContextParam[];
  disabled?: boolean;
  hideRecordType?: boolean;
  extraFieldsConfigReference?: ExtraFieldsConfigReferenceInput;
  hideEmptyLookups?: boolean;
};

export const ExtraFieldsAreaContent: React.FC<
  React.PropsWithChildren<Props>
> = ({
  availableRecordTypes,
  formFields,
  contextParams,
  disabled,
  hideRecordType = false,
  extraFieldsConfigReference,
  hideEmptyLookups = false,
}) => {
  const { watch } = useFormContext();
  const recordTypeName = watch(recordTypeFormKey);

  if (!formFields.length) {
    return null;
  }
  const visibility = new ExtraFieldsVisibility();

  return (
    <>
      {!hideRecordType &&
        getRecordTypeFormControl(availableRecordTypes, disabled)}
      {formFields
        .filter((f) =>
          visibility.isFieldVisible(f, {
            recordTypeName,
            contextParams: contextParams ?? [],
          }),
        )
        .map(
          (
            {
              fieldApiName,
              fieldInfo,
              required,
              id,
              visibleLines,
              disabled: fieldDisabled,
            },
            index,
          ) => {
            const fieldDisabledValue = fieldDisabled ?? disabled ?? false;
            const inputFieldInfo = {
              name: getExtraFieldInputName(fieldApiName, fieldInfo.type),
              label: fieldInfo.label,
              type: fieldInfo.type,
              hint: fieldInfo.inlineHelpText ?? undefined,
              disabled: fieldDisabledValue,
              minRows: visibleLines ?? undefined,
              step: toHtmlInputStepAttribute(fieldInfo.decimalPlaces),
              required,
              options:
                fieldInfo.picklistValues?.map((pv) => ({
                  label: pv.label,
                  value: pv.value,
                })) || [],
            };
            if (fieldInfo.type === 'REFERENCE') {
              return (
                <ExtraFieldLookup
                  key={index}
                  fieldId={id}
                  disabled={fieldDisabledValue}
                  contextParams={contextParams}
                  fieldInfo={inputFieldInfo}
                  extraFieldsConfigReference={extraFieldsConfigReference}
                  hideWhenEmpty={hideEmptyLookups}
                />
              );
            }
            if (fieldInfo.type === 'TEXTAREA' && fieldInfo.isRichTextArea) {
              return (
                <RichtextInput
                  key={index}
                  name={inputFieldInfo.name}
                  label={inputFieldInfo.label}
                  required={required}
                  disabled={fieldDisabledValue}
                  hint={inputFieldInfo.hint}
                />
              );
            }
            return <FieldInput key={index} fieldInfo={inputFieldInfo} />;
          },
        )}
    </>
  );
};

export const Loading: React.FC<React.PropsWithChildren<unknown>> = () => (
  <div className="my-3">
    <Skeleton height={40} className="mt-3" />
    <Skeleton height={40} className="mt-3" />
    <Skeleton height={40} className="mt-3" />
  </div>
);

export function getRecordTypeFormControl(
  availableRecordTypes: readonly AvailableRecordTypeFragment[],
  disabled?: boolean,
) {
  if (availableRecordTypes.length === 0) {
    return <ControlledInput name={recordTypeFormKey} type="hidden" />;
  }
  const dropDownfieldInfo = {
    name: recordTypeFormKey,
    label: i18next.t('extraFields.recordType'),
    type: FieldType.Picklist,
    disabled: disabled || availableRecordTypes.length === 1,
    required: true,
    doNotInsertDefaultOptions: true,
    options: availableRecordTypes.map((rt) => ({
      label: rt.Name,
      value: rt.DeveloperName,
    })),
  };
  return <FieldInput fieldInfo={dropDownfieldInfo} />;
}
